import {
    Component,
    ViewChild,
    AfterViewInit,
    ChangeDetectorRef,
    Input,
    inject,
    ChangeDetectionStrategy,
} from '@angular/core';
import { TiktokOpenService } from 'src/app/shared/services/tiktok/tiktok-open.service';
import { OrderStatus } from 'src/app/shared/services/tiktok/enums/order-status';
import {
    set as _set,
    get as _get,
    filter as _filter,
    each as _each,
    map as _map,
    trim as _trim,
    groupBy as _groupBy,
} from 'lodash';
import { Subject } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { DxDataGridComponent } from 'devextreme-angular';
import {
    MessageType,
    UtilService,
    NotificationService,
} from 'src/app/shared/services';
import { PrintSheetUtils } from 'src/app/shared/services/print-sheet/utils';
import moment from 'moment';
import { DxToolbarComponent } from 'devextreme-angular/ui/toolbar';
import {
    DxiItemComponent,
    DxiColumnComponent,
    DxoFilterRowComponent,
    DxoPagingComponent,
    DxoPagerComponent,
    DxoSummaryComponent,
    DxiTotalItemComponent,
} from 'devextreme-angular/ui/nested';
import { DxTemplateDirective } from 'devextreme-angular/core';
import { DxTabsComponent } from 'devextreme-angular/ui/tabs';
import { DxDataGridComponent as DxDataGridComponent_1 } from 'devextreme-angular/ui/data-grid';
import { DxFormComponent } from 'devextreme-angular/ui/form';
import { DatePipe } from '@angular/common';
import { FormatNumberPipe } from '../../pipes/format-number.pipe';

@Component({
    selector: 'app-tiktok-orders',
    templateUrl: './tiktok-orders.component.html',
    styleUrls: ['./tiktok-orders.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DxToolbarComponent,
        DxiItemComponent,
        DxTemplateDirective,
        DxTabsComponent,
        DxDataGridComponent_1,
        DxFormComponent,
        DxiColumnComponent,
        DxoFilterRowComponent,
        DxoPagingComponent,
        DxoPagerComponent,
        DxoSummaryComponent,
        DxiTotalItemComponent,
        DatePipe,
        FormatNumberPipe,
    ],
})
export class TiktokOrdersComponent implements AfterViewInit {
    private cdref = inject(ChangeDetectorRef);
    private tiktokOpenService = inject(TiktokOpenService);
    private notificationService = inject(NotificationService);
    private utilService = inject(UtilService);

    @ViewChild('dataGrid', { static: true })
    private dataGrid!: DxDataGridComponent;
    @Input()
    title = 'TikTok';

    isDataLoading = false;
    hasRemarks = false;
    orderSource: any[] = [];
    orderStatuses = [
        {
            key: OrderStatus.AwaitingShipment,
            text: 'Awaiting Shipment',
        },
        {
            key: OrderStatus.AwaitingCollection,
            text: 'Awaiting Collection',
        },
        {
            key: OrderStatus.InTransit,
            text: 'In Transit',
        },
        {
            key: OrderStatus.Delivered,
            text: 'Delivered',
        },
        {
            key: OrderStatus.Completed,
            text: 'Completed',
        },
        {
            key: OrderStatus.Cancelled,
            text: 'Cancelled',
        },
        {
            key: OrderStatus.Unpaid,
            text: 'Unpaid',
        },
        {
            key: OrderStatus.OnHold,
            text: 'On Hold',
        },
    ];
    selectedTabItem:
        | {
              key: OrderStatus;
              text: string;
          }
        | undefined;
    refreshButtonOptions = {
        text: '',
        icon: 'refresh',
        onClick: (): void => {
            this.updateOrderSubject.next();
        },
    };

    private updateOrderSubject = new Subject<any>();

    ngAfterViewInit(): void {
        if (!this.selectedTabItem) {
            this.selectedTabItem = this.getTabByStatus(
                OrderStatus.AwaitingShipment,
            );
            this.cdref.detectChanges();
        }
        this.updateOrderSubject
            .pipe(
                switchMap((orderStatus: OrderStatus) => {
                    this.dataGridBeginLoading();
                    if (!orderStatus) {
                        orderStatus = this.selectedTabItem!.key;
                        this.cdref.detectChanges();
                    }
                    return this.tiktokOpenService.getOrderList(orderStatus);
                }),
            )
            .subscribe(
                (response: any) => {
                    const orders: any[] = _get(response, 'orders') || [];
                    this.fillDateFields(orders);
                    this.fillMatchingNumber(orders);
                    this.fillGroupedOrderItems(orders);
                    this.fillItemsCount(orders);
                    this.computeHasRemark(orders);
                    this.orderSource = orders;
                    this.dataGrid.instance.refresh();
                    this.dataGridEndLoading();
                },
                (error) => {
                    console.error(error);
                    this.notificationService.pushMessage({
                        displayTime: 6000,
                        message: this.utilService.errorToString(error),
                        messageType: MessageType.error,
                    });
                    this.dataGridEndLoading();
                },
            );
        this.updateOrderSubject.next();
    }

    orderStatusSelectionChanged(event: any): void {
        const orderStatusKey: OrderStatus = _get(event, 'addedItems[0].key');
        this.updateOrderSubject.next(orderStatusKey);
    }

    private fillDateFields(orders: any[]) {
        const dateFields: string[] = ['create_time', 'update_time'];
        _each(orders, (order) => {
            _each(dateFields, (field: string) => {
                if (order[field]) {
                    _set(
                        order,
                        `$${field}_date`,
                        moment.unix(order[field]).toDate(),
                    );
                }
            });
        });
    }

    private fillMatchingNumber(orders: any[]) {
        _each(orders, (order) => {
            if (order.id) {
                order.matching = PrintSheetUtils.generateMatchingNumber(
                    `${order.id}`,
                );
            } else {
                order.matching = '';
            }
        });
    }

    private fillGroupedOrderItems(orders: any[]) {
        _each(orders, (order: any) => {
            let itemNo = 0;
            const groupedOrderItems = _groupBy(order.line_items, 'seller_sku');
            _set(
                order,
                'groupedOrderItems',
                _map(groupedOrderItems, (_orderItems: any[], sellerSku) => {
                    const candidateItem = _orderItems[0];
                    const quantity = _orderItems.length;
                    const unitPrice = this.utilService.localStringToNumber(
                        candidateItem.sale_price,
                    );
                    return {
                        itemNo: ++itemNo,
                        description: PrintSheetUtils.constructItemDescription(
                            sellerSku,
                            candidateItem.product_name,
                            quantity,
                            candidateItem.sku_name,
                        ),
                        unitPrice,
                        quantity,
                        amount: unitPrice * quantity,
                    };
                }),
            );
        });
    }

    private fillItemsCount(orders: any[]) {
        _each(orders, (order: any) => {
            _set(order, 'items_count', (order.line_items || []).length);
        });
    }

    private computeHasRemark(orders: any[]) {
        this.hasRemarks = false;
        _each(orders, (order) => {
            if (_trim(order.buyer_message)) {
                this.hasRemarks = true;
            }
        });
    }

    private getTabByStatus(orderStatus: OrderStatus): any {
        return _filter(this.orderStatuses, {
            key: orderStatus,
        })[0];
    }

    private dataGridBeginLoading(): void {
        this.dataGrid.instance.beginCustomLoading('');
        this.isDataLoading = true;
        this.cdref.detectChanges();
    }

    private dataGridEndLoading(): void {
        this.dataGrid.instance.endCustomLoading();
        this.isDataLoading = false;
        this.cdref.detectChanges();
    }
}
